import { useCallback, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import type {
  ConstructDefinition,
  ConstructValidateResponse,
  ConstructPreviewResponse,
  ConstructSaveResult,
  JobConstruct,
} from '../types/gotypes'

// useConstructs (T17) wraps the construct routes documented in
// design/2026-09-15-constructs-and-sav-import.md ("Go HTTP routes" —
// Construct group). It is deliberately thin: every guardrail lives in
// goapi/pkg/constructs.Service, this hook only shapes requests/responses and
// surfaces the documented error codes so ConstructEditorDialog can map them
// to a field or a banner.

// ConstructApiError normalises a failed request into the shape the editor
// needs: the documented `error` code plus whatever detail rides with it.
export interface ConstructApiError {
  code: string
  message?: string
  lineErrors?: { index: number; message: string }[]
  errors?: string[]
  path?: string[]
  dependents?: string[]
  status?: number
}

function toConstructApiError(err: any): ConstructApiError {
  const body = err?.response?.data
  if (body && typeof body === 'object' && typeof body.error === 'string') {
    return {
      code: body.error,
      message: body.message,
      lineErrors: body.lineErrors,
      errors: body.errors,
      path: body.path,
      dependents: body.dependents,
      status: err.response?.status,
    }
  }
  return { code: 'internal', message: err?.message || String(err), status: err?.response?.status }
}

export const useConstructs = ({ customer, job }: { customer: string, job: string }) => {
  const [constructs, setConstructs] = useState<JobConstruct[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<ConstructApiError | null>(null)

  const baseUrl = `${API_BASE_URL}/job/${customer}/${job}/constructs`

  const list = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await axios.get<JobConstruct[]>(baseUrl)
      setConstructs(res.data || [])
      return res.data || []
    } catch (err) {
      const apiErr = toConstructApiError(err)
      setError(apiErr)
      throw apiErr
    } finally {
      setIsLoading(false)
    }
  }, [baseUrl])

  const validate = useCallback(async (definition: ConstructDefinition) => {
    setIsValidating(true)
    try {
      const res = await axios.post<ConstructValidateResponse>(`${baseUrl}/validate`, definition)
      return res.data
    } catch (err) {
      throw toConstructApiError(err)
    } finally {
      setIsValidating(false)
    }
  }, [baseUrl])

  const preview = useCallback(async (definition: ConstructDefinition) => {
    setIsPreviewing(true)
    try {
      const res = await axios.post<ConstructPreviewResponse>(`${baseUrl}/preview`, definition)
      return res.data
    } catch (err) {
      throw toConstructApiError(err)
    } finally {
      setIsPreviewing(false)
    }
  }, [baseUrl])

  const create = useCallback(async (definition: ConstructDefinition, folder: string) => {
    setIsSaving(true)
    setError(null)
    try {
      const res = await axios.post<ConstructSaveResult>(baseUrl, { definition, folder })
      return res.data
    } catch (err) {
      const apiErr = toConstructApiError(err)
      setError(apiErr)
      throw apiErr
    } finally {
      setIsSaving(false)
    }
  }, [baseUrl])

  const update = useCallback(async (name: string, definition: ConstructDefinition, folder: string) => {
    setIsSaving(true)
    setError(null)
    try {
      const res = await axios.put<ConstructSaveResult>(`${baseUrl}/${encodeURIComponent(name)}`, { definition, folder })
      return res.data
    } catch (err) {
      const apiErr = toConstructApiError(err)
      setError(apiErr)
      throw apiErr
    } finally {
      setIsSaving(false)
    }
  }, [baseUrl])

  const remove = useCallback(async (name: string) => {
    setIsSaving(true)
    setError(null)
    try {
      await axios.delete(`${baseUrl}/${encodeURIComponent(name)}`)
    } catch (err) {
      const apiErr = toConstructApiError(err)
      setError(apiErr)
      throw apiErr
    } finally {
      setIsSaving(false)
    }
  }, [baseUrl])

  return {
    constructs,
    isLoading,
    isValidating,
    isPreviewing,
    isSaving,
    error,
    list,
    validate,
    preview,
    create,
    update,
    remove,
  }
}

export default useConstructs
